import type { CSSProperties, ElementType, ReactNode } from "react";

import { cn } from "../lib/cn";

export interface EmptyStateProps {
  icon: ElementType;
  title: string;
  description: string;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ icon: Icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex min-h-72 flex-col items-center justify-center px-6 py-14 text-center",
        className,
      )}
    >
      <div className="grid h-11 w-11 place-items-center rounded-lg bg-surface-elevated text-muted">
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <h2 className="mt-5 text-base font-semibold text-primary">{title}</h2>
      <p className="mt-3 max-w-sm text-prose leading-relaxed text-muted">{description}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export interface SkeletonProps {
  className?: string;
  style?: CSSProperties;
}

/** One loading placeholder shape; size and radius come from `className`. */
export function Skeleton({ className, style }: SkeletonProps) {
  return <span aria-hidden="true" className={cn("skeleton block rounded", className)} style={style} />;
}

export interface ListSkeletonProps {
  rows?: number;
  label?: string;
  className?: string;
}

/** Simulated record list: one card per row, identity on the left, metadata on the right. */
export function ListSkeleton({ rows = 5, label = "Loading content", className }: ListSkeletonProps) {
  return (
    <div className={cn("grid gap-2", className)} role="status" aria-label={label} aria-busy="true">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="rounded-lg border border-border bg-surface px-4 py-3" aria-hidden="true">
          <div className="flex items-center gap-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-5 w-20" />
            <Skeleton className="ml-auto h-5 w-28" />
          </div>
          <Skeleton className={cn("mt-3 h-3", index % 2 ? "w-1/2" : "w-2/3")} />
        </div>
      ))}
    </div>
  );
}
